import platform from '@tencent/wphk-platform-adapter';

/** 缓存数据结构（带过期时间） */
interface CacheData<T> {
  value: T;
  expireAt?: number;
}

/**
 * 从本地存储获取数据
 */
export async function getItem<T>(key: string): Promise<T | null | undefined> {
  try {
    const data = await platform.localStorage.getItem(key);
    if (!data) return null;
    // 特殊处理 'undefined' 字符串
    if (data === 'undefined') return undefined;
    return JSON.parse(data);
  } catch {
    return null;
  }
}

/**
 * 向本地存储设置数据
 */
export function setItem(key: string, value: any): void {
  try {
    const data = JSON.stringify(value);
    // 处理 JSON.stringify(undefined) 返回 undefined 的情况
    if (data === undefined) {
      platform.localStorage.setItem(key, 'undefined');
    } else {
      platform.localStorage.setItem(key, data);
    }
  } catch (error) {
    throw error;
  }
}

/**
 * 从本地存储获取数据（支持过期时间）
 */
export async function getItemSync<T>(key: string): Promise<T | null> {
  try {
    const data = await platform.localStorage.getItem(key);
    if (!data) return null;
    const parsed = JSON.parse(data);
    // 兼容 CacheData 结构和普通数据
    if (parsed && typeof parsed === 'object' && 'value' in parsed) {
      const cacheData = parsed as CacheData<T>;
      // 检查是否过期
      if (cacheData.expireAt && Date.now() > cacheData.expireAt) {
        platform.localStorage.removeItem(key);
        return null;
      }
      return cacheData.value;
    }
    // 普通数据直接返回
    return parsed as T;
  } catch {
    return null;
  }
}

/**
 * 同步向本地存储设置数据（支持过期时间）
 * @param key 存储键
 * @param value 存储值
 * @param expireTime 过期时间（毫秒），可选
 */
export function setItemSync<T>(key: string, value: T, expireTime?: number): void {
  try {
    const cacheData: CacheData<T> = expireTime
      ? { value, expireAt: Date.now() + expireTime }
      : { value };
    platform.localStorage.setItem(key, JSON.stringify(cacheData));
  } catch (error) {
    throw error;
  }
}
